/**
 * EnvironmentDetector - Multi-signal LIFF vs Browser detection at startup.
 *
 * Detects whether the app is running inside LINE LIFF or a standard browser
 * using multiple signals: window.liff presence, user agent, and URL parameters.
 *
 * Requirements: 1.1, 2.1, 25.1
 */

export type Environment = 'liff' | 'browser';

let detectedEnvironment: Environment | null = null;

/**
 * Checks if the LIFF SDK object is available on the window.
 */
function hasLiffSdk(): boolean {
  try {
    return typeof window !== 'undefined' && 'liff' in window;
  } catch {
    return false;
  }
}

/**
 * Checks if the user agent indicates LINE's in-app browser.
 */
function hasLineUserAgent(): boolean {
  try {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('line/') || ua.includes('liff');
  } catch {
    return false;
  }
}

/**
 * Checks if LIFF-specific URL parameters are present.
 * LIFF apps receive `liff.state` or `liff_id` in the URL.
 */
function hasLiffUrlParams(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.has('liff.state') || params.has('liff_id');
  } catch {
    return false;
  }
}

/**
 * Performs multi-signal detection to determine the runtime environment.
 * Uses a scoring approach: if any signal indicates LIFF, we classify as LIFF.
 */
function detect(): Environment {
  let liffSignals = 0;

  if (hasLiffSdk()) liffSignals++;
  if (hasLineUserAgent()) liffSignals++;
  if (hasLiffUrlParams()) liffSignals++;

  // If at least one signal indicates LIFF, classify as LIFF
  return liffSignals >= 1 ? 'liff' : 'browser';
}

/**
 * Initializes environment detection. Should be called once at app startup.
 * Stores the result for the session.
 */
export function initEnvironment(): Environment {
  detectedEnvironment = detect();
  return detectedEnvironment;
}

/**
 * Returns true if running inside LINE LIFF.
 */
export function isLiff(): boolean {
  if (detectedEnvironment === null) {
    initEnvironment();
  }
  return detectedEnvironment === 'liff';
}

/**
 * Returns true if running in a standard browser (not LIFF).
 */
export function isBrowser(): boolean {
  if (detectedEnvironment === null) {
    initEnvironment();
  }
  return detectedEnvironment === 'browser';
}

/**
 * Returns the detected environment type.
 */
export function getEnvironment(): Environment {
  if (detectedEnvironment === null) {
    initEnvironment();
  }
  return detectedEnvironment!;
}

/**
 * Resets detection (useful for testing).
 */
export function resetEnvironment(): void {
  detectedEnvironment = null;
}
